<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import searchIcon from "@iconify/icons-mdi/magnify";
  import deleteIcon from "@iconify/icons-mdi/delete-outline";
  import folderIcon from "@iconify/icons-mdi/folder-outline";
  import keyStoreIcon from "@iconify/icons-mdi/key-variant";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import addIcon from "@iconify/icons-mdi/plus";
  import closeIcon from "@iconify/icons-mdi/close";
  import databaseIcon from "@iconify/icons-mdi/database-outline";
  import chevronUpIcon from "@iconify/icons-mdi/chevron-up";
  import chevronDownIcon from "@iconify/icons-mdi/chevron-down";

  import { getApiErrorMessage } from "./lib/apiError.js";
  import { t } from "./lib/i18n.js";
  import KeyDetail from "./KeyDetail.svelte";

  export let apiBase = "";
  export let selectedHostId = "";
  export let apiKey = "";
  export let locale = "en";
  export let authMode = false;
  export let connected = false;
  export let paused = false;
  export let setRefresh = () => {};
  export let onApiError = (msg) => {};
  export let onUnauthorized = () => {};

  $: browseBase = selectedHostId ? `${apiBase}/browse/${encodeURIComponent(selectedHostId)}` : apiBase + "/browse/localhost%3A6379";

  let databases = [];
  let selectedDb = 0;
  /** Current page keys (single page when using prev/next; or all loaded when using "load more" style) */
  let keys = [];
  /** Page cache for prev: each item is { keys, nextCursor } */
  let pageCache = [];
  let currentPageIndex = 0;
  let cursor = 0;
  let hasMore = false;
  /** Total keys in DB when pattern is * (from backend) */
  let totalKeys = null;
  let searchPattern = "*";
  let sortBy = "name"; // 'name' | 'nameDesc' | 'type' | 'typeDesc'
  let loading = false;
  let error = "";

  let selectedKey = null;
  let keyDetail = null;

  let addKeyOpen = false;
  let addKeyName = "";
  let addKeyValue = "";
  let addKeySaving = false;
  let addKeyError = "";

  /** Delete confirmation: { type: 'key', key } | { type: 'pattern', pattern } | { type: 'result', deleted } | null */
  let deleteDialog = null;
  let deleteDialogSaving = false;

  let serverInfo = null;
  let pollInterval = null;
  /** Last time DB list / server info / keys were updated (poll or manual refresh) */
  let lastUpdated = null;
  /** Keys per page from backend (for pagination label) */
  let pageSize = 25;

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function fetchDatabases() {
    if (!browseBase) return;
    try {
      const res = await fetch(`${browseBase}/databases`, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }
      if (!res.ok) {
        onApiError(`${res.status} ${res.statusText}`);
        connected = false;
        return;
      }
      const data = await res.json();
      const next = data.databases || [];
      const same = JSON.stringify(databases) === JSON.stringify(next);
      if (!same) {
        databases = next;
        lastUpdated = new Date();
      }
      connected = true;
    } catch (e) {
      error = e.message;
      onApiError(e.message);
      connected = false;
    }
  }

  async function fetchServerInfo() {
    if (!browseBase) return;
    try {
      const res = await fetch(`${browseBase}/info`, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }
      if (!res.ok) {
        onApiError(`${res.status} ${res.statusText}`);
        return;
      }
      const next = await res.json();
      const same = JSON.stringify(serverInfo) === JSON.stringify(next);
      if (!same) {
        serverInfo = next;
        lastUpdated = new Date();
      }
      connected = true;
    } catch (e) {
      connected = false;
    }
  }

  async function fetchKeys(reset = false) {
    if (loading) return;

    const dbAtStart = selectedDb;
    loading = true;
    error = "";

    try {
      const c = reset ? 0 : (pageCache.length > 0 ? pageCache[pageCache.length - 1].nextCursor : cursor);
      const url = new URL(`${browseBase}/db${dbAtStart}/keys`, location.origin);
      url.searchParams.set("pattern", searchPattern || "*");
      url.searchParams.set("cursor", c.toString());
      // count omitted: backend uses STORE_KEYS_PAGE_SIZE (default 25)

      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (selectedDb !== dbAtStart) return;

      const raw = data.keys || [];
      const nextKeys = raw.map((k) => {
        if (typeof k === "string") return { key: k, type: "string", ttl: -1 };
        const ttl = k.ttl != null ? Number(k.ttl) : -1;
        return { key: k.key, type: (k.type || "string").toLowerCase(), ttl };
      });

      if (data.page_size > 0) pageSize = data.page_size;
      totalKeys = data.total != null ? Number(data.total) : null;
      cursor = data.cursor || 0;
      hasMore = data.has_more;

      if (reset) {
        pageCache = [{ keys: nextKeys, nextCursor: cursor }];
        currentPageIndex = 0;
        keys = nextKeys;
      } else {
        pageCache = [...pageCache, { keys: nextKeys, nextCursor: cursor }];
        currentPageIndex = pageCache.length - 1;
        keys = nextKeys;
      }

      lastUpdated = new Date();
      connected = true;
    } catch (e) {
      if (selectedDb !== dbAtStart) return;
      error = e.message;
      onApiError(e.message);
      connected = false;
    } finally {
      loading = false;
    }
  }

  async function selectKey(key, isRetry = false) {
    selectedKey = key;
    keyDetail = null;
    error = "";

    try {
      const url = `${browseBase}/db${selectedDb}/key/${encodeURIComponent(key)}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }

      if (!res.ok) {
        if ((res.status === 404 || res.status === 412) && !isRetry) {
          await new Promise((r) => setTimeout(r, 150));
          return selectKey(key, true);
        }
        if (res.status === 404 || res.status === 412) {
          selectedKey = null;
          keyDetail = null;
          keys = keys.filter((k) => k.key !== key);
          onApiError(await getApiErrorMessage(res, locale));
          return;
        }
        onApiError(await getApiErrorMessage(res, locale));
        keyDetail = null;
        return;
      }
      keyDetail = await res.json();
    } catch (e) {
      onApiError(e?.message || String(e));
      keyDetail = null;
    }
  }

  function openDeleteKeyDialog(key) {
    deleteDialog = { type: "key", key };
  }

  async function confirmDeleteKey() {
    if (!deleteDialog || deleteDialog.type !== "key") return;
    const key = deleteDialog.key;
    deleteDialogSaving = true;
    try {
      const url = `${browseBase}/db${selectedDb}/key/${encodeURIComponent(key)}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Accept: "application/json", ...authHeaders() }
      });
      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }
      if (!res.ok) {
        onApiError(await getApiErrorMessage(res, locale));
        return;
      }
      keys = keys.filter((k) => k.key !== key);
      if (selectedKey === key) {
        selectedKey = null;
        keyDetail = null;
      }
      deleteDialog = null;
    } catch (e) {
      error = e.message;
    } finally {
      deleteDialogSaving = false;
    }
  }

  function openMassDeleteDialog() {
    deleteDialog = { type: "pattern", pattern: searchPattern };
  }

  async function confirmMassDelete() {
    if (!deleteDialog || deleteDialog.type !== "pattern") return;
    const pattern = (deleteDialog.pattern || "").trim() || "*";
    deleteDialogSaving = true;
    try {
      const res = await fetch(`${browseBase}/db${selectedDb}/keys`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({ pattern })
      });
      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      deleteDialog = { type: "result", deleted: data.deleted };
      fetchKeys(true);
    } catch (e) {
      error = e.message;
      deleteDialog = null;
    } finally {
      deleteDialogSaving = false;
    }
  }

  function closeDeleteDialog() {
    deleteDialog = null;
  }

  function refresh() {
    fetchDatabases();
    fetchServerInfo();
    fetchKeys(true);
  }

  function handleSearch(e) {
    e.preventDefault();
    fetchKeys(true);
  }

  async function refreshCurrentKeysPage() {
    if (loading || !browseBase) return;
    const dbAtStart = selectedDb;
    const c = currentPageIndex === 0 ? 0 : (pageCache[currentPageIndex - 1]?.nextCursor ?? 0);
    try {
      const url = new URL(`${browseBase}/db${dbAtStart}/keys`, location.origin);
      url.searchParams.set("pattern", searchPattern || "*");
      url.searchParams.set("cursor", String(c));
      const res = await fetch(url.toString(), { headers: { Accept: "application/json", ...authHeaders() } });
      if (res.status !== 200 || selectedDb !== dbAtStart) return;
      const data = await res.json();
      const raw = data.keys || [];
      const nextKeys = raw.map((k) => {
        if (typeof k === "string") return { key: k, type: "string", ttl: -1 };
        const ttl = k.ttl != null ? Number(k.ttl) : -1;
        return { key: k.key, type: (k.type || "string").toLowerCase(), ttl };
      });
      const same = JSON.stringify(nextKeys) === JSON.stringify(keys) && (data.cursor || 0) === cursor && !!data.has_more === hasMore && (data.total == null ? totalKeys : Number(data.total)) === totalKeys;
      if (same) return;
      const nextCursor = data.cursor || 0;
      const newTotal = data.total != null ? Number(data.total) : null;
      if (data.page_size > 0) pageSize = data.page_size;
      totalKeys = newTotal;
      cursor = nextCursor;
      hasMore = data.has_more;
      const newCache = [...pageCache];
      newCache[currentPageIndex] = { keys: nextKeys, nextCursor };
      pageCache = newCache;
      keys = nextKeys;
      lastUpdated = new Date();
    } catch (_) {}
  }

  function startPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(() => {
      if (!paused) {
        fetchDatabases();
        fetchServerInfo();
        refreshCurrentKeysPage();
      }
    }, 250);
  }

  let prevSelectedDb = undefined;
  let prevSelectedHostId = undefined;

  onMount(() => {
    setRefresh(refresh);
    if (selectedHostId) {
      prevSelectedHostId = selectedHostId;
      fetchDatabases();
      fetchServerInfo();
      fetchKeys(true);
    }
    startPolling();
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
  });

  function goToPrevPage() {
    if (currentPageIndex <= 0) return;
    currentPageIndex--;
    keys = pageCache[currentPageIndex].keys;
  }

  function goToNextPage() {
    if (currentPageIndex < pageCache.length - 1) {
      currentPageIndex++;
      keys = pageCache[currentPageIndex].keys;
      return;
    }
    if (hasMore && !loading) fetchKeys(false);
  }

  async function submitAddKey() {
    const keyName = (addKeyName || "").trim();
    if (!keyName) {
      addKeyError = t(locale, "browse_key_name_required");
      return;
    }
    addKeySaving = true;
    addKeyError = "";
    try {
      const url = `${browseBase}/db${selectedDb}/key/${encodeURIComponent(keyName)}`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({ value: addKeyValue || "", ttl: 0 })
      });
      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }
      if (res.status === 201) {
        addKeyOpen = false;
        addKeyName = "";
        addKeyValue = "";
        fetchKeys(true);
        return;
      }
      if (!res.ok) {
        addKeyError = await getApiErrorMessage(res, locale);
        return;
      }
    } catch (e) {
      addKeyError = e?.message || String(e);
    } finally {
      addKeySaving = false;
    }
  }

  // When host changes: clear everything and refetch (new connection)
  $: if (selectedHostId && selectedHostId !== prevSelectedHostId) {
    prevSelectedHostId = selectedHostId;
    prevSelectedDb = undefined;
    keys = [];
    pageCache = [];
    currentPageIndex = 0;
    cursor = 0;
    hasMore = false;
    totalKeys = null;
    selectedKey = null;
    keyDetail = null;
    databases = [];
    error = "";
    fetchDatabases();
    fetchServerInfo();
    fetchKeys(true);
  }

  // When DB changes (or initial load): clear list so we never show another DB's keys, then refetch
  $: if (selectedHostId && selectedDb !== undefined && prevSelectedDb !== selectedDb) {
    prevSelectedDb = selectedDb;
    keys = [];
    pageCache = [];
    currentPageIndex = 0;
    cursor = 0;
    hasMore = false;
    totalKeys = null;
    fetchKeys(true);
  }

  function getTypeBadgeClass(type) {
    const known = ["string", "list", "set", "zset", "hash", "stream"];
    return known.includes(type) ? `type-${type}` : "type-module";
  }

  /** Split key name into segments so only the literal "tag:" (with colon) is sky blue; rest white */
  function getKeyNameSegments(keyName) {
    if (!keyName || typeof keyName !== "string") return [{ text: String(keyName), highlight: false }];
    const parts = keyName.split("tag:");
    if (parts.length <= 1) return [{ text: keyName, highlight: false }];
    const segs = [];
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] !== "") segs.push({ text: parts[i], highlight: false });
      if (i < parts.length - 1) segs.push({ text: "tag:", highlight: true });
    }
    return segs.length ? segs : [{ text: keyName, highlight: false }];
  }

  // Group keys by namespace (colon-separated)
  function getNamespace(key) {
    const parts = key.split(":");
    if (parts.length > 1) {
      return parts.slice(0, -1).join(":");
    }
    return null;
  }

  function sortKeyItems(items) {
    const a = [...items];
    if (sortBy === "name") a.sort((x, y) => x.key.localeCompare(y.key));
    else if (sortBy === "nameDesc") a.sort((x, y) => y.key.localeCompare(x.key));
    else if (sortBy === "type") a.sort((x, y) => x.type.localeCompare(y.type) || x.key.localeCompare(y.key));
    else if (sortBy === "typeDesc") a.sort((x, y) => y.type.localeCompare(x.type) || x.key.localeCompare(y.key));
    return a;
  }

  function displayType(type) {
    if (!type) return "";
    return String(type).toUpperCase();
  }

  /** Human-readable TTL in list (matches sidebar "Expires in" format) */
  function formatTTLList(ttl) {
    if (ttl == null || ttl < 0) return t(locale, "browse_no_expiry");
    if (ttl === 0) return "0s";
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
    if (ttl < 86400) return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
    return `${Math.floor(ttl / 86400)}d ${Math.floor((ttl % 86400) / 3600)}h`;
  }

  $: sortedKeys = sortKeyItems(keys);

  /** Build a tree from keys (colon-separated = path) and flatten to rows with depth for hierarchy */
  function buildKeyTreeRows(items) {
    const root = { children: {}, items: [] };
    for (const item of items) {
      const parts = item.key.split(":");
      let node = root;
      for (let i = 0; i < parts.length; i++) {
        const seg = parts[i];
        const isLast = i === parts.length - 1;
        if (!node.children[seg]) node.children[seg] = { children: {}, items: [] };
        node = node.children[seg];
        if (isLast) node.items.push(item);
      }
    }
    const rows = [];
    function walk(n, depth, path) {
      const segs = Object.keys(n.children || {}).sort();
      for (const seg of segs) {
        const child = n.children[seg];
        const fullPath = path ? path + ":" + seg : seg;
        const hasChildren = Object.keys(child.children || {}).length > 0;
        const hasItems = (child.items && child.items.length > 0);
        if (hasChildren || hasItems) {
          rows.push({ depth, type: "folder", label: seg, path: fullPath + ":" });
        }
        walk(child, depth + 1, fullPath);
      }
      for (const item of n.items || []) {
        rows.push({ depth, type: "key", item });
      }
    }
    walk(root, 0, "");
    return rows;
  }

  $: keyTreeRows = buildKeyTreeRows(sortedKeys);

  /** Sidebar: single expand/collapse for all empty DB runs (icon next to DATABASES only) */
  let allEmptyExpanded = false;
  $: hasEmptyRuns = (() => {
    const sorted = [...databases].sort((a, b) => a.index - b.index);
    let run = 0;
    for (const db of sorted) {
      if (db.keys > 0) run = 0;
      else { run++; if (run === 1) return true; }
    }
    return false;
  })();
  $: dbSidebarItems = (() => {
    const out = [];
    const sorted = [...databases].sort((a, b) => a.index - b.index);
    let run = [];
    for (const db of sorted) {
      if (db.keys > 0) {
        if (run.length > 0) {
          out.push({ type: "emptyRun", start: run[0].index, end: run[run.length - 1].index, dbs: run });
          run = [];
        }
        out.push({ type: "db", db });
      } else {
        run.push(db);
      }
    }
    if (run.length > 0) {
      out.push({ type: "emptyRun", start: run[0].index, end: run[run.length - 1].index, dbs: run });
    }
    return out;
  })();
</script>

<main class="w-full mt-6 flex-1 min-h-0 flex flex-col gap-4">
  <!-- Header -->
  <section class="ui-card p-5 sm:p-6 flex-shrink-0">
    <div class="flex items-start justify-between gap-4">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-slate-300/60 bg-slate-600/50 text-slate-50"
        >
          <Icon icon={keyStoreIcon} width="18" height="18" />
        </span>
        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            <span class="text-zinc-500">db{selectedDb}</span> / {t(locale, "nav_browse").toLowerCase()}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">{t(locale, "browse_subtitle")}</p>
        </div>
      </div>
      <div class="flex items-center gap-3">
        {#if lastUpdated}
          <span class="text-xs text-zinc-500 tabular-nums" title={t(locale, "browse_last_update")}>{t(locale, "browse_updated", { time: lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })}</span>
        {/if}
        <button
          type="button"
          class="add-key-btn text-sm py-1.5 px-3 inline-flex items-center gap-2 border border-[rgb(var(--stash-accent))]/40 bg-[rgb(var(--stash-accent))]/15 text-white hover:bg-[rgb(var(--stash-accent))]/25 hover:border-[rgb(var(--stash-accent))]/60 transition-colors rounded-lg font-medium"
          title={t(locale, "browse_add_key")}
          onclick={() => { addKeyOpen = true; addKeyError = ''; addKeyName = ''; addKeyValue = ''; }}
        >
          <Icon icon={addIcon} width="18" height="18" />
          {t(locale, "browse_add_key")}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-300 hover:text-white transition-colors"
          title={t(locale, "browse_refresh_view")}
          onclick={() => refresh()}
          disabled={loading}
        >
          <Icon icon={refreshIcon} width="18" height="18" />
        </button>
      </div>
    </div>
  </section>

  <!-- Sidebar + Key list: row on lg, column on small -->
  <div class="flex flex-col lg:flex-row flex-1 min-h-0 gap-4">
  <!-- Left: Database sidebar (tall enough for ~40 DBs; empty runs collapsed) -->
  <aside
    class="flex-shrink-0 w-full lg:w-40 sticky top-20 self-start rounded-lg border border-white/10 bg-black/80 overflow-hidden"
    aria-label={t(locale, "browse_databases")}
  >
    <div class="px-2.5 py-2 border-b border-white/10 bg-black/40 backdrop-blur-sm z-10 flex items-center gap-2">
      <Icon icon={databaseIcon} width="16" height="16" class="text-zinc-500 flex-shrink-0" aria-hidden="true" />
      <h2 class="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex-1">{t(locale, "browse_databases")}</h2>
      {#if hasEmptyRuns}
        <button
          type="button"
          class="p-0.5 rounded transition-colors {allEmptyExpanded ? 'text-emerald-400 hover:bg-emerald-400/10' : 'text-rose-400 hover:bg-rose-400/10'}"
          onclick={() => (allEmptyExpanded = !allEmptyExpanded)}
          title={allEmptyExpanded ? t(locale, "browse_collapse_empty") : t(locale, "browse_expand_empty")}
          aria-label={allEmptyExpanded ? t(locale, "browse_collapse_empty") : t(locale, "browse_expand_empty")}
        >
          <Icon icon={allEmptyExpanded ? chevronUpIcon : chevronDownIcon} width="16" height="16" />
        </button>
      {/if}
    </div>
    <nav class="py-1 px-1.5 flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible lg:min-h-[min(40rem,calc(100vh-8rem))] lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto">
      {#each dbSidebarItems as item}
        {#if item.type === "db"}
          {@const db = item.db}
          <button
            type="button"
            class="browse-db-item flex-shrink-0 lg:w-full text-left px-2.5 py-1.5 rounded-lg font-mono flex items-center justify-between gap-2 {selectedDb === db.index ? 'browse-db-selected' : 'text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200 border border-transparent'}"
            onclick={() => (selectedDb = db.index)}
          >
            <span class="truncate">db{db.index}</span>
            <span class="flex-shrink-0 tabular-nums text-xs {selectedDb === db.index ? 'text-white/90' : db.keys > 0 ? 'text-zinc-300' : 'text-zinc-500'}">{db.keys}</span>
          </button>
        {:else}
          {#if allEmptyExpanded}
            {#each item.dbs as db}
              <button
                type="button"
                class="browse-db-item flex-shrink-0 lg:w-full text-left px-2.5 py-1.5 rounded-lg font-mono flex items-center justify-between gap-2 {selectedDb === db.index ? 'browse-db-selected' : 'text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-300 border border-transparent'}"
                onclick={() => (selectedDb = db.index)}
              >
                <span class="truncate">db{db.index}</span>
                <span class="flex-shrink-0 text-xs text-zinc-500">0</span>
              </button>
            {/each}
          {:else}
            <div
              class="browse-db-item flex-shrink-0 lg:w-full px-2.5 py-1.5 rounded-lg text-xs text-zinc-500 border border-transparent"
              role="presentation"
            >
              <span class="truncate">{t(locale, "browse_empty_range", { start: item.start, end: item.end, count: item.dbs.length })}</span>
            </div>
          {/if}
        {/if}
      {/each}
      {#if databases.length === 0 && !loading}
        <div class="px-2.5 py-3 text-zinc-500">{t(locale, "browse_no_databases")}</div>
      {/if}
    </nav>
  </aside>

  <!-- Center: Key list + detail (stack on small, row on lg) -->
  <div class="flex-1 min-w-0 flex flex-col lg:flex-row min-h-0 gap-4">
  <div class="flex-1 min-w-0 flex flex-col min-h-0">
    <div class="ui-card flex flex-col flex-1 min-h-0 bg-black/95 lg:bg-transparent">
      <!-- Search + actions bar: tighter on mobile -->
      <div class="p-3 sm:p-4 border-b border-white/10 flex-shrink-0">
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <form onsubmit={handleSearch} class="flex-1 flex gap-2 min-w-0">
            <div class="flex-1 relative min-w-0">
              <Icon
                icon={searchIcon}
                class="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 flex-shrink-0"
                width="18"
                height="18"
              />
              <input
                type="text"
                bind:value={searchPattern}
                placeholder={t(locale, "browse_pattern_placeholder")}
                class="w-full min-w-0 h-10 pl-10 pr-4 bg-black/30 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-400/20"
              />
            </div>
            <button type="submit" class="ui-btn flex-shrink-0">{t(locale, "browse_search")}</button>
          </form>
          <button
            type="button"
            class="ui-btn text-rose-400 hover:text-rose-300 flex-shrink-0 self-start sm:self-auto"
            onclick={openMassDeleteDialog}
            title={t(locale, "browse_mass_delete")}
          >
            <Icon icon={deleteIcon} width="18" height="18" />
          </button>
        </div>
        <div class="mt-2 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
          {#if keys.length > 0 || hasMore}
            {@const start = totalKeys != null ? currentPageIndex * pageSize + 1 : 1}
            {@const end = currentPageIndex * pageSize + keys.length}
            {@const totalStr = totalKeys != null ? totalKeys.toLocaleString() : null}
            <div class="flex flex-wrap items-center gap-2 min-w-0">
              <span class="text-xs text-zinc-500 truncate hidden sm:inline">
                {#if totalStr != null}
                  {t(locale, "browse_of_total", { start, end, total: totalStr })}
                {:else}
                  {t(locale, "browse_loaded_range", { start, end })}
                  {#if hasMore}<span class="text-zinc-400"> · {t(locale, "browse_more")}</span>{/if}
                {/if}
              </span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="ui-btn text-sm py-1.5 px-2 sm:px-3"
                  disabled={currentPageIndex <= 0}
                  onclick={goToPrevPage}
                  title={t(locale, "browse_previous_page")}
                >
                  {t(locale, "browse_prev")}
                </button>
                <button
                  type="button"
                  class="ui-btn text-sm py-1.5 px-2 sm:px-3"
                  disabled={(!hasMore && currentPageIndex >= pageCache.length - 1) || loading}
                  onclick={goToNextPage}
                  title={t(locale, "browse_next_page")}
                >
                  {loading ? "…" : t(locale, "browse_next")}
                </button>
              </div>
            </div>
            <div class="flex items-center gap-1 rounded-lg border border-white/10 bg-black/20 p-0.5 hidden sm:flex">
              <span class="px-2 py-1 text-xs text-zinc-500 self-center">{t(locale, "browse_sort")}</span>
              <div class="flex rounded-md overflow-hidden">
                <div class="flex border-r border-white/10">
                  <button
                    type="button"
                    class="px-2 py-1 text-xs font-medium transition-colors {sortBy === 'name' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}"
                    title={t(locale, "browse_sort_name_asc")}
                    onclick={() => (sortBy = 'name')}
                  >{t(locale, "browse_name_up")}</button>
                  <button
                    type="button"
                    class="px-2 py-1 text-xs font-medium transition-colors {sortBy === 'nameDesc' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}"
                    title={t(locale, "browse_sort_name_desc")}
                    onclick={() => (sortBy = 'nameDesc')}
                  >↓</button>
                </div>
                <div class="flex">
                  <button
                    type="button"
                    class="px-2 py-1 text-xs font-medium transition-colors {sortBy === 'type' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}"
                    title={t(locale, "browse_sort_type_asc")}
                    onclick={() => (sortBy = 'type')}
                  >{t(locale, "browse_type_up")}</button>
                  <button
                    type="button"
                    class="px-2 py-1 text-xs font-medium transition-colors {sortBy === 'typeDesc' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}"
                    title={t(locale, "browse_sort_type_desc")}
                    onclick={() => (sortBy = 'typeDesc')}
                  >↓</button>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>

      <!-- Key list: scrollable, paginated; opaque on mobile to avoid transparency over background -->
      <div class="flex-1 min-h-0 overflow-y-auto bg-black/95 lg:bg-transparent">
          {#if keys.length === 0 && !loading}
            <div class="p-8 text-center text-zinc-500">
              {t(locale, "browse_no_keys")}
            </div>
          {/if}

          <!-- Hierarchical key tree (folders + keys with depth indent) -->
          {#each keyTreeRows as row}
            {#if row.type === "folder"}
              <div
                class="border-b border-white/5 px-4 py-2 bg-white/[0.02] flex items-center gap-2 text-zinc-400"
                style="padding-left: calc(1rem + {row.depth * 16}px);"
              >
                <Icon icon={folderIcon} width="16" height="16" class="flex-shrink-0" />
                <span class="text-xs font-medium">{row.label}:</span>
              </div>
            {:else}
              {@const item = row.item}
              <button
                type="button"
                class="w-full py-2 sm:py-3 text-left border-b border-white/5 hover:bg-white/[0.03] transition-colors flex items-center gap-2 sm:gap-3 min-w-0 {selectedKey === item.key ? 'key-row-selected' : ''}"
                style="padding-left: calc(0.75rem + {row.depth * 12}px);"
                onclick={() => selectKey(item.key)}
              >
                <span class="px-1.5 py-0.5 rounded text-[10px] font-medium {getTypeBadgeClass(item.type)} flex-shrink-0 hidden sm:inline" title={t(locale, "browse_value_type")}>{displayType(item.type)}</span>
                <span class="font-mono text-xs sm:text-sm truncate flex-1 min-w-0 block">
                  {#each getKeyNameSegments(item.key) as seg}
                    <span class={seg.highlight ? 'text-sky-400' : 'text-zinc-300'}>{seg.text}</span>
                  {/each}
                </span>
                <span class="text-xs text-zinc-500 tabular-nums flex-shrink-0 hidden md:inline" title={t(locale, "browse_ttl")}>{formatTTLList(item.ttl)}</span>
                <span
                  role="button"
                  tabindex="0"
                  class="p-1 text-rose-400 hover:text-rose-300 transition-colors cursor-pointer flex-shrink-0"
                  onclick={(e) => { e.stopPropagation(); openDeleteKeyDialog(item.key); }}
                  onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openDeleteKeyDialog(item.key); } }}
                >
                  <Icon icon={deleteIcon} width="16" height="16" />
                </span>
              </button>
            {/if}
          {/each}

          <!-- Pagination (repeated at bottom for long lists) -->
          {#if keys.length > 0 || hasMore}
            {@const startBot = totalKeys != null ? currentPageIndex * pageSize + 1 : 1}
            {@const endBot = currentPageIndex * pageSize + keys.length}
            {@const totalStrBot = totalKeys != null ? totalKeys.toLocaleString() : null}
            <div class="sticky bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-black/90 flex flex-wrap items-center justify-between gap-2">
              <span class="text-xs text-zinc-500">
                {#if totalStrBot != null}
                  {t(locale, "browse_showing_total", { start: startBot.toLocaleString(), end: endBot.toLocaleString(), total: totalStrBot })}
                {:else}
                  {t(locale, "browse_showing_loaded", { start: startBot.toLocaleString(), end: endBot.toLocaleString() })}
                  {#if hasMore} · {t(locale, "browse_more")} {:else} · {t(locale, "browse_all_for_pattern")} {/if}
                {/if}
              </span>
              <div class="flex items-center gap-1">
                <button type="button" class="ui-btn text-sm py-1.5 px-3" disabled={currentPageIndex <= 0} onclick={goToPrevPage}>{t(locale, "browse_previous_page")}</button>
                <button type="button" class="ui-btn text-sm py-1.5 px-3" disabled={(!hasMore && currentPageIndex >= pageCache.length - 1) || loading} onclick={goToNextPage}>{loading ? t(locale, "loading") : t(locale, "browse_next")}</button>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Key Detail: full overlay on mobile; sticky sidebar on desktop -->
    {#if selectedKey}
      <!-- Mobile: full-screen opaque backdrop so dashboard is not visible underneath -->
      <div class="fixed inset-0 z-40 bg-black lg:hidden" aria-hidden="true"></div>
      <div class="fixed inset-0 z-50 lg:relative lg:inset-auto min-w-0 w-full lg:min-w-[280px] lg:w-[min(100%,640px)] xl:w-[640px] flex-shrink-0 flex flex-col min-h-0 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-5rem)] overflow-y-auto order-last lg:order-none bg-black lg:bg-transparent">
        <KeyDetail
          {apiBase}
          browseBase={browseBase}
          {apiKey}
          {selectedDb}
          key={selectedKey}
          detail={keyDetail}
          {locale}
          onClose={() => { selectedKey = null; keyDetail = null; }}
          onDelete={() => openDeleteKeyDialog(selectedKey)}
          onRefresh={() => selectKey(selectedKey)}
          {onUnauthorized}
        />
      </div>
    {/if}

    <!-- Add key drawer: same layout as key detail (overlay on mobile, sidebar on desktop) -->
    {#if addKeyOpen}
      <!-- Mobile: full-screen opaque backdrop so dashboard is not visible underneath -->
      <div class="fixed inset-0 z-40 bg-black lg:hidden" aria-hidden="true"></div>
      <div class="fixed inset-0 z-50 lg:relative lg:inset-auto min-w-0 w-full lg:min-w-[280px] lg:w-[min(100%,640px)] xl:w-[640px] flex-shrink-0 flex flex-col min-h-0 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-5rem)] overflow-y-auto order-last lg:order-none bg-black lg:bg-transparent border border-white/10 rounded-lg overflow-hidden">
        <div class="flex items-center justify-between gap-2 p-4 border-b border-white/10 bg-black/40">
          <h2 class="text-lg font-semibold text-white">{t(locale, "browse_add_key_title")}</h2>
          <button type="button" class="p-2 hover:bg-white/10 rounded-lg text-zinc-400 hover:text-white" onclick={() => { addKeyOpen = false; addKeyError = ''; }} title={t(locale, "browse_close")}>
            <Icon icon={closeIcon} width="20" height="20" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-4">
          {#if addKeyError}
            <div class="mb-4 text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded-lg px-3 py-2">{addKeyError}</div>
          {/if}
          <p class="text-xs text-zinc-500 mb-2">{t(locale, "browse_key_name_hint")}</p>
          <input
            type="text"
            class="w-full h-10 px-4 bg-black/30 border border-white/10 rounded-lg text-white font-mono text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-400/20 mb-4"
            placeholder={t(locale, "browse_key_name_placeholder")}
            bind:value={addKeyName}
          />
          <p class="text-xs text-zinc-500 mb-2">{t(locale, "browse_type_string")}</p>
          <textarea
            class="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white font-mono text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-400/20 resize-y min-h-[120px] mb-4"
            placeholder={t(locale, "browse_value_placeholder")}
            bind:value={addKeyValue}
          ></textarea>
          <div class="flex gap-2">
            <button type="button" class="ui-btn" onclick={submitAddKey} disabled={addKeySaving}>{addKeySaving ? t(locale, "browse_saving") : t(locale, "browse_save_key")}</button>
            <button type="button" class="ui-btn border-white/10 bg-white/[0.03]" onclick={() => { addKeyOpen = false; addKeyError = ''; }}>{t(locale, "browse_cancel")}</button>
          </div>
        </div>
      </div>
    {/if}
  </div>
  </div>

  <!-- Delete confirmation dialog (Tailwind, no alert/confirm) -->
  {#if deleteDialog}
    <div
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-dialog-title"
      tabindex="-1"
      onclick={() => { if (!deleteDialogSaving) closeDeleteDialog(); }}
      onkeydown={(e) => { if (e.key === 'Escape' && !deleteDialogSaving) closeDeleteDialog(); }}
    >
      <div
        class="bg-zinc-900 border border-white/10 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        role="presentation"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => e.stopPropagation()}
      >
        {#if deleteDialog.type === "key"}
          <div class="p-5">
            <h2 id="delete-dialog-title" class="text-lg font-semibold text-zinc-100 mb-2">{t(locale, "browse_delete_key_title")}</h2>
            <p class="text-sm text-zinc-400 mb-4 break-all font-mono">"{deleteDialog.key}"</p>
            <div class="flex justify-end gap-2">
              <button type="button" class="ui-btn border-white/10 bg-white/[0.03]" onclick={closeDeleteDialog} disabled={deleteDialogSaving}>{t(locale, "browse_cancel")}</button>
              <button type="button" class="ui-btn bg-rose-600 hover:bg-rose-500 text-white border-rose-500/50" onclick={confirmDeleteKey} disabled={deleteDialogSaving}>{deleteDialogSaving ? t(locale, "browse_deleting") : t(locale, "browse_delete")}</button>
            </div>
          </div>
        {:else if deleteDialog.type === "pattern"}
          <div class="p-5">
            <h2 id="delete-dialog-title" class="text-lg font-semibold text-zinc-100 mb-2">{t(locale, "browse_delete_keys_title")}</h2>
            <p class="text-sm text-zinc-400 mb-3">{t(locale, "browse_delete_keys_hint")}</p>
            <input
              type="text"
              class="w-full h-10 px-4 bg-black/30 border border-white/10 rounded-lg text-white font-mono text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-rose-400/20 mb-4"
              placeholder={t(locale, "browse_delete_pattern_placeholder")}
              bind:value={deleteDialog.pattern}
            />
            <div class="flex justify-end gap-2">
              <button type="button" class="ui-btn border-white/10 bg-white/[0.03]" onclick={closeDeleteDialog} disabled={deleteDialogSaving}>{t(locale, "browse_cancel")}</button>
              <button type="button" class="ui-btn bg-rose-600 hover:bg-rose-500 text-white border-rose-500/50" onclick={confirmMassDelete} disabled={deleteDialogSaving}>{deleteDialogSaving ? t(locale, "browse_deleting") : t(locale, "browse_delete_all_matching")}</button>
            </div>
          </div>
        {:else if deleteDialog.type === "result"}
          <div class="p-5">
            <h2 id="delete-dialog-title" class="text-lg font-semibold text-zinc-100 mb-2">{t(locale, "browse_keys_deleted")}</h2>
            <p class="text-sm text-zinc-400 mb-4">{t(locale, "browse_deleted_count", { count: deleteDialog.deleted.toLocaleString() })}</p>
            <div class="flex justify-end">
              <button type="button" class="ui-btn" onclick={closeDeleteDialog}>{t(locale, "browse_close")}</button>
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</main>
